// vue
import { reactive } from 'vue';

// types
import { Props, IBar, CurrentOrder } from '../../../types';

// useComputed
import { useComputed } from './computed';

// useGlobals
import { useGlobals } from './globals';

export function useOrders(props: Props) {
    // useComputed
    const { theme } = useComputed(props);

    // useGlobals
    const {
        barsGl,
        widget,
        timeFrame,
        priceOffsets,
        chartContainer,
        widgetOrders,
        processing,
        orderBackgrounds,
        historyOrdersQueue,
    } = useGlobals(props);

    // изменение оформление ордеров при смене темы
    const changeThemeOfOrders = () => {
        const value = Object.values(widgetOrders);

        for (const order of value) {
            if (order?.setQuantityBackgroundColor) {
                order.setQuantityBackgroundColor(orderBackgrounds[theme.value]);
            }
        }
    };

    // очищаем заранее расчитанные отступы для каждого ордера
    const resetPriceOffsets = async () => {
        Object.keys(priceOffsets).forEach(key => {
            delete priceOffsets[key];
        });
    };

    // очищаем список виджетов на графике
    const resetwidgetOrders = async () => {
        Object.keys(widgetOrders).forEach(key => {
          widgetOrders[key]?.remove?.();
          widgetOrders[`${key}__overlay`]?.remove?.();

          
          if (!!widgetOrders[key] && typeof widgetOrders[key] == 'string') {
            widget.value?.activeChart().removeEntity(widgetOrders[String(key)]);
          }

          if (!!widgetOrders[`${key}__overlay`] && typeof widgetOrders[`${key}__overlay`] == 'string') {
          widget.value?.activeChart().removeEntity(widgetOrders[`${key}__overlay`]);
          }

          delete widgetOrders[key];
          delete widgetOrders[`${key}__overlay`];
        });
    };

    // отрисовка ордеров
    const renderOrders = async (hardReset = false) => {
        if (processing.renderOrders == true) {
            return;
        }

        processing.renderOrders = true;

        // сбрасываем отступы иконок buy/sell
        resetPriceOffsets();
    
        // очищаем очередь неотрисованных ордеров
        historyOrdersQueue.value = [];
    
        if (hardReset) {
            await resetwidgetOrders();
        }
    
        // проверяем, есть ли ордер на графике в списке ордеров
        if (Object.keys(widgetOrders).length) {
            for (const key of Object.keys(widgetOrders)) {
                const idx = Object.keys(props.data.orders).findIndex(el => el == key);
    
                // если данного оредера больше не существует
                // тогда удаляем его из графика
                if (!~idx) {
                    console.log('widgetOrders[key]: ', widgetOrders[key]);
                    widgetOrders[key]?.remove?.();
                    widget.value?.activeChart().removeEntity(widgetOrders[key]);
                    delete widgetOrders[key];
                }
            }
        }
    
        const entries = Object.entries(props.data.orders);
    
        for (const [key, value] of entries) {
            await setOrder(key, value);
        }

        processing.renderOrders = false;
    };
    
    // нормализация времени
    function normalizeToSeconds(timestamp: number) {
        if (timestamp > 999999999999) {
            return Math.floor(timestamp / 1000);
        }
        // Если уже в секундах
        return timestamp;
    }
    
    // получение полной информации о конкретном баре
    const getBar = async (timeFrom: number | undefined): Promise<IBar | undefined> => {
        timeFrom = normalizeToSeconds(timeFrom ?? 0);
    
        const bar = barsGl[timeFrame.value].find((el: any, idx: number) => {
            const time = normalizeToSeconds(el.time);
            const time2 = normalizeToSeconds(barsGl[timeFrame.value][idx + 1]?.time);
                
            return idx == barsGl[timeFrame.value].length - 1
                ? (timeFrom ?? 0) > time
                : (timeFrom ?? 0) > time && (timeFrom ?? 0) < time2;
        });
    
        return bar;
    };

    const getPriceToBarRatio = (
        bar: IBar, 
        chartContainerHeight: number, 
        priceRange: { min: number, max: number },
    ): number => {
        // 1. Высота текущего бара в ценах
        const barHeight = bar.high - bar.low;
      
        // 2. Высота видимой области в ценах
        const visiblePriceRange = priceRange.max - priceRange.min;
      
        // 3. Соотношение высоты бара к видимой области
        const barToVisibleRatio = barHeight / visiblePriceRange;
      
        // 4. Высота контейнера графика в пикселях (например, 500px)
        const pixelsPerPriceUnit = chartContainerHeight / visiblePriceRange;
      
        // 5. Желаемый отступ в пикселях (например, 20px)
        const desiredPixelOffset = 20;
      
        // 6. Конвертируем пиксели в цену
        return desiredPixelOffset / pixelsPerPriceUnit;
    };
    
    // вычисление отступа для иконки buy/sell
    // в том случае, если она оказалась поверха бара
    const getPriceWithOffset = async (bar: IBar, price: number, orderType = 'buy'): Promise<number | null> => {
        if (!bar) return null;

        const chart = widget.value?.chart();
        
        const priceRange = (chart as any).getVisiblePriceRange();
        const chartHeight = (chartContainer.value?.clientHeight ?? 700) - 46;
      
        // Рассчитываем динамический отступ
        const offset = getPriceToBarRatio(bar, chartHeight, {
            min: priceRange.from,
            max: priceRange.to,
        });
  
        const key = `${bar.time}_${orderType}`;
      
        if (!priceOffsets[key]) {
            priceOffsets[key] = offset;
        } else {
            priceOffsets[key] += offset;
        }
      
        return orderType === 'buy' 
            ? bar.low - priceOffsets[key] 
            : bar.high + priceOffsets[key];
    };
    
    // создание исторических ордеров
    const createHistoryOrder = async (key: string, value: CurrentOrder) => {
        const orderType = value?.orderType;
        const icon = orderType == 'buy' ? 0xf062 : 0xf063;
        const mainColor = orderType
            ? orderType == 'buy' ? '#059669' : '#EF4444'
            : '#FBBF24';
    
        // исторические ордера
        if (widgetOrders[key] == undefined) {
            const bar = await getBar(value.time);

            if (bar) {
                // const priceWithOffset = await getPriceWithOffset(bar, value.price, orderType) || value.price;
    
                widgetOrders[key] = widget.value?.chart().createShape(
                    { time: value.time, price: value.price },
                    {
                        shape: 'icon',
                        icon,
                        lock: true,
                        zOrder: 'top',
                        disableUndo: true,
                        disableSave: true,
                        disableSelection: true,
                        overrides: {
                            color: theme.value == 'dark' ? '#202020' : '#fff',
                            size: 22,
                        },
                    },
                );

                widgetOrders[`${key}__overlay`] = widget.value?.chart().createShape(
                    { time: value.time, price: value.price },
                    {
                        shape: 'icon',
                        icon,
                        lock: true,
                        zOrder: 'top',
                        disableUndo: true,
                        disableSave: true,
                        disableSelection: true,
                        overrides: {
                            color: mainColor,
                            size: 16,
                        },
                    },
                );
            } else {
                historyOrdersQueue.value.push({ key, ...value});
            }
        } else {
            // widgetOrders[key]
            //     .setPrice(value.price)
            //     .setTime(value.time);
        }
    };
    
    const setOrder = async (key: string, value: CurrentOrder) => {
        const isPnl = key === 'pnl';
        const isHistoryOrder = key.includes('history__');

        if (isHistoryOrder) {
            await createHistoryOrder(key, value);
            return;
        }

        const orderType = value?.orderType;

        const quantityPositive = (value.value ?? 0) >= 0;
        const quantityText = quantityPositive ? `+${value.value}` : String(value.value ?? 0);

        const mainColor =
    isPnl
        ? (quantityPositive ? '#059669' : '#EF4444')
        : orderType
            ? (orderType === 'buy' ? '#059669' : '#EF4444')
            : '#FBBF24';

        // 1) price — только number
        const priceNum = Number(value.price);
        if (!Number.isFinite(priceNum)) {
            console.warn('Skip order line: bad price', { key, price: value.price, value });
            return;
        }

        // 2) chart должен существовать
        const w = widget.value;
        const chart = w?.chart?.();
        if (!chart) {
            // виджет/чарт ещё не готов
            return;
        }

        // 3) создаём/берём линию
        let line = widgetOrders[key];

        if (!line) {
            try {
                line = chart.createOrderLine();
            } catch (e) {
                // console.info('createOrderLine failed', e);
                return;
            }

            line
                .setTooltip('Additional order information')
                .setModifyTooltip('Modify order')
                .setCancelTooltip('Cancel order')
                .setBodyTextColor('#FFFFFF')
                .setQuantityBackgroundColor(orderBackgrounds[theme.value]);

            widgetOrders[key] = line;
        }

        // 4) обновляем price
        line.setPrice(priceNum);

        // 5) оформление
        if (isPnl) {
            line
                .setText('PNL: ')
                .setQuantity(`${quantityText}%`)
                .setLineColor(mainColor)
                .setBodyBorderColor(mainColor)
                .setBodyBackgroundColor(mainColor)
                .setQuantityTextColor(mainColor)
                .setQuantityBorderColor(mainColor);
        } else {
            line
                .setText(orderType ?? '')
                .setQuantity(`${value.value ?? ''}`)
                .setLineColor(mainColor)
                .setBodyBorderColor(mainColor)
                .setBodyBackgroundColor(mainColor)
                .setQuantityTextColor(mainColor)
                .setQuantityBorderColor(mainColor);
        }
    };
    
    return {
        changeThemeOfOrders,
        renderOrders,
        resetwidgetOrders,
        createHistoryOrder,
    };
}